import { Canceler } from 'axios';
import { action, computed, makeObservable, observable } from 'mobx';
import { defaultPagination } from 'src/constants/main';
import client from 'src/services/client';
import { IPagination } from 'src/types/main';
import { getCancelToken } from 'src/utils/helpers';
import { defaultMenuGroupsFilters } from 'src/view/screens/MenuManagementScreen/screens/MenuGroupsScreen/constants/menuGropus';
import { IMenuGroup, IMenuGroupsFilters } from 'src/view/screens/MenuManagementScreen/screens/MenuGroupsScreen/types/menuGroups';
import { localeStore } from '~/mobx/localesStore';
import logger from '~/services/logger';
import { toastSuccess } from '~/utils/toasts';

class MenuGroupsStore {
  constructor() {
    makeObservable(this);
  }

  cancelStack: {
    [key: string]: Canceler;
  } = {};

  @observable pagination: IPagination<IMenuGroup> = defaultPagination;
  @observable filters: IMenuGroupsFilters = defaultMenuGroupsFilters;
  @action setFilters = (filters: Partial<IMenuGroupsFilters>) => {
    this.filters = { ...this.filters, ...filters };
    this.fetchMenuGroups(1);
  };

  get noMenuGroupsFound(): boolean {
    return !this.filters.search && !this.pagination.loading && !this.pagination.data.length;
  }

  @computed get isLoading(): boolean {
    return Boolean(this.pagination.loading || this.pagination.refreshing);
  }

  @action fetchMenuGroups = async (page: number) => {
    try {
      const cancelId = 'fetchCategories';
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');

      this.pagination.loading = true;
      this.pagination.refreshing = true;
      const pagination = await client.get('v3/menu_groups', {
        params: {
          search: this.filters.search,
          sort_field: this.filters.sortField || undefined,
          sort_dir: this.filters.sortDir || undefined,
          page,
        },
        headers: { 'Accept-Language': 'en,ar' },
        cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)),
      });
      this.pagination = {
        ...pagination,
        refreshing: false,
        loading: false,
      };
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.pagination = {
        ...this.pagination,
        refreshing: false,
        loading: false,
      };
      logger.error({ error, place: 'MenuGroupsStore fetchMenuGroups' });
    }
  };

  @action deleteMenuGroup = async (menuGroupId: number) => {
    try {
      this.pagination.refreshing = true;

      await client.delete(`v3/menu_groups/${menuGroupId}`);

      this.fetchMenuGroups(this.pagination.current_page);

      toastSuccess({ message: localeStore.t('toast_menu_group_deleted') });
    } catch (error: any) {
      this.pagination.refreshing = false;
      logger.error({ error, place: 'MenuGroupsStore deleteMenuGroup', showModal: true });
    }
  };

  @action clearFilters = () => {
    this.filters = defaultMenuGroupsFilters;
  };
}

const menuGroupsStore = new MenuGroupsStore();

export default menuGroupsStore;
